import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  AlertTitle,
  Card,
  Table,
  Stack,
  Container,
  Typography,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
} from '@mui/material';
import { toast } from 'react-toastify';

import { listWorkspaces, createWorkspace, deleteWorkspace } from '../services/workspaceService';
import LoadingSpinner from '../components/LoadingSpinner';
import Iconify from '../components/iconify';
import Scrollbar from '../components/scrollbar';

export default function WorkspaceListPage() {
  const navigate = useNavigate();

  const [showPreviewWarning, setShowPreviewWarning] = useState(() => !localStorage.getItem('vpsi_workspace_preview_dismissed'));
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('updatedAt');
  const [selected, setSelected] = useState([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const fetchWorkspaces = async () => {
    try {
      const data = await listWorkspaces();
      setWorkspaces(data);
      setLoading(false);
    } catch (err) {
      toast.error('Failed to load workspaces');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWorkspaces();
  }, []);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(workspaces.map((w) => w.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (id) => {
    const idx = selected.indexOf(id);
    if (idx === -1) {
      setSelected([...selected, id]);
    } else {
      setSelected(selected.filter((s) => s !== id));
    }
  };

  const handleCreate = async () => {
    try {
      await createWorkspace(newName, newDescription);
      setCreateDialogOpen(false);
      setNewName('');
      setNewDescription('');
      await fetchWorkspaces();
      toast.success('Workspace created');
    } catch (err) {
      toast.error('Failed to create workspace');
    }
  };

  const handleConfirmDelete = async () => {
    try {
      await Promise.all(selected.map((id) => deleteWorkspace(id)));
      setSelected([]);
      setDeleteDialogOpen(false);
      await fetchWorkspaces();
      toast.success('Workspace(s) deleted');
    } catch (err) {
      toast.error('Failed to delete workspace(s)');
    }
  };

  const sortedWorkspaces = [...workspaces].sort((a, b) => {
    const valA = a[orderBy] || '';
    const valB = b[orderBy] || '';
    if (order === 'asc') return valA > valB ? 1 : -1;
    return valA < valB ? 1 : -1;
  });

  const paginatedWorkspaces = sortedWorkspaces.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) return <LoadingSpinner message="Loading workspaces..." />;

  return (
    <>
      <Helmet>
        <title>VPSI | Workspaces</title>
      </Helmet>
      <Container>
        {showPreviewWarning && (
          <Alert
            severity="warning"
            onClose={() => { setShowPreviewWarning(false); localStorage.setItem('vpsi_workspace_preview_dismissed', 'true'); }}
            sx={{ mb: 3 }}
          >
            <AlertTitle>Early Feature Preview</AlertTitle>
            This is a very early feature preview. There might be bugs with the workspaces feature and we are currently trying to address them. Please let Kyle know at{' '}
            <a href="mailto:kyle.fischer@videoplussensors.com">kyle.fischer@videoplussensors.com</a> if there are any that you come across.
          </Alert>
        )}

        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Typography variant="h4" gutterBottom>
            Workspaces
          </Typography>
          <Stack direction="row" spacing={1}>
            {selected.length > 0 && (
              <Button color="error" variant="outlined" startIcon={<Iconify icon="eva:trash-2-outline" />} onClick={() => setDeleteDialogOpen(true)}>
                Delete ({selected.length})
              </Button>
            )}
            <Button variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} onClick={() => setCreateDialogOpen(true)}>
              New Workspace
            </Button>
          </Stack>
        </Stack>

        <Card>
          <Scrollbar>
            <TableContainer sx={{ minWidth: 800 }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={selected.length > 0 && selected.length < workspaces.length}
                        checked={workspaces.length > 0 && selected.length === workspaces.length}
                        onChange={handleSelectAll}
                      />
                    </TableCell>
                    {[
                      { id: 'name', label: 'Name' },
                      { id: 'description', label: 'Description' },
                      { id: 'recordingIds', label: 'Recordings' },
                      { id: 'updatedAt', label: 'Last Modified' },
                    ].map((col) => (
                      <TableCell key={col.id}>
                        <TableSortLabel
                          active={orderBy === col.id}
                          direction={orderBy === col.id ? order : 'asc'}
                          onClick={() => handleRequestSort(col.id)}
                        >
                          {col.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedWorkspaces.map((workspace) => {
                    const isSelected = selected.indexOf(workspace.id) !== -1;
                    return (
                      <TableRow
                        hover
                        key={workspace.id}
                        selected={isSelected}
                        onClick={() => navigate(`/dashboard/workspace/${workspace.id}`)}
                        sx={{ cursor: 'pointer' }}
                      >
                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                          <Checkbox checked={isSelected} onChange={() => handleSelect(workspace.id)} />
                        </TableCell>
                        <TableCell>{workspace.name}</TableCell>
                        <TableCell>{workspace.description}</TableCell>
                        <TableCell>{workspace.recordingIds?.length || 0}</TableCell>
                        <TableCell>
                          {workspace.updatedAt ? new Date(workspace.updatedAt).toLocaleDateString() : '—'}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {paginatedWorkspaces.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                        No workspaces yet. Create one to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={workspaces.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => { setPage(0); setRowsPerPage(parseInt(e.target.value, 10)); }}
          />
        </Card>
      </Container>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Workspace</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={newDescription}
            onChange={(e) => setNewDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={!newName.trim()}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete {selected.length} workspace{selected.length !== 1 ? 's' : ''}? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleConfirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
